import { getPostSignOutNavigationTarget, isEditTreePath } from '../routing/routing';

export type MenuUser = {
  email?: string | null;
} | null;

export interface MenuAuthViewState {
  email: string | null;
  isAuthenticated: boolean;
  showCreateTree: boolean;
  showHeader: boolean;
  showMyTrees: boolean;
  showSignIn: boolean;
  showSignOut: boolean;
}

export interface CompactMenuStateOptions {
  canEdit: boolean;
  hasSaveAction: boolean;
  isDraft: boolean;
  isEditMode: boolean;
  isLoading: boolean;
  isSaving: boolean;
  showRenameTreeInMenu: boolean;
  showSaveButton: boolean;
  treeId?: string;
  user: MenuUser;
  hasDeployAction?: boolean;
  isDeploying?: boolean;
}

export interface CompactMenuViewState {
  auth: MenuAuthViewState;
  modeToggleLabel: 'Edit' | 'Preview';
  modeToggleTarget: string | null;
  saveButtonLabel: 'Publish' | 'Publishing...';
  showDraftStatus: boolean;
  showModeToggle: boolean;
  showRenameTree: boolean;
  showSaveButton: boolean;
  statusLabel: 'Draft';
  showDeploy: boolean;
  deployLabel: 'Deploy' | 'Deploying…';
}

export class MenuInteractor {
  getAuthMenuState(user: MenuUser): MenuAuthViewState {
    const isAuthenticated = user != null;

    return {
      email: user?.email ?? null,
      isAuthenticated,
      showCreateTree: isAuthenticated,
      showHeader: isAuthenticated,
      showMyTrees: isAuthenticated,
      showSignIn: !isAuthenticated,
      showSignOut: isAuthenticated,
    };
  }

  getCompactMenuState(options: CompactMenuStateOptions): CompactMenuViewState {
    const auth = this.getAuthMenuState(options.user);
    const modeToggleTarget = this.getModeToggleNavigationTarget(options.treeId, options.isEditMode);

    return {
      auth,
      modeToggleLabel: options.isEditMode ? 'Preview' : 'Edit',
      modeToggleTarget,
      saveButtonLabel: options.isSaving ? 'Publishing...' : 'Publish',
      showDraftStatus: !options.isLoading && options.isDraft,
      showModeToggle:
        !options.isLoading &&
        auth.isAuthenticated &&
        options.canEdit &&
        modeToggleTarget !== null,
      showRenameTree:
        !options.isLoading &&
        options.canEdit &&
        options.isEditMode &&
        options.showRenameTreeInMenu,
      showSaveButton:
        !options.isLoading && options.showSaveButton && options.hasSaveAction,
      statusLabel: 'Draft',
      showDeploy:
        !options.isLoading &&
        options.isEditMode &&
        options.canEdit &&
        options.hasDeployAction === true,
      deployLabel: options.isDeploying === true ? 'Deploying…' : 'Deploy',
    };
  }

  getModeToggleNavigationTarget(treeId: string | undefined, isEditMode: boolean): string | null {
    if (!treeId) {
      return null;
    }

    return isEditMode ? `/tree/${treeId}` : `/tree/${treeId}/edit`;
  }

  getPostSignOutNavigationTarget(pathname: string): string | null {
    return getPostSignOutNavigationTarget(pathname);
  }

  isEditRoute(pathname: string): boolean {
    return isEditTreePath(pathname);
  }
}
